import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import {
  Link,
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { get } from "api";
import { Button } from "atoms/button/Button";
import Dropdown from "atoms/dropdown/Dropdown";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";

const load = async (membershipId: string) => {
  const userMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  const membershipCancellationReasons = queryClient.fetchQuery(
    ["/platform-user-memberships/cancellation-reasons"],
    () => get("/platform-user-memberships/cancellation-reasons")
  );

  const userInfo = queryClient.fetchQuery(["/user"], () => get("/user"));

  return waitObj({
    userMemberships,
    userInfo,
    membershipId,
    membershipCancellationReasons,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipCancelLoader: LoaderFunction = ({
  params: { membershipId },
}) => {
  if (!membershipId) return redirect("..");

  return load(membershipId);
};

export const WalletMembershipCancelPage = () => {
  const data = useLoaderData() as LoaderData;

  const navigate = useNavigate();

  const { data: userMemberships } = useQuery({
    queryKey: ["/platform-user-memberships"],
    queryFn: () => get("/platform-user-memberships"),
    initialData: data.userMemberships,
  });

  const membership = userMemberships.find(
    (m) => m.membership.id == data.membershipId
  );

  const cancellationReasons = data.membershipCancellationReasons.map((r) => ({
    ...r,
    name: r.reason,
  }));

  const [selectedCancellationReasonId, setSelectedCancellationReasonId] =
    useState<string>();
  const selectedCancellationReason = cancellationReasons.find(
    (r) => r.id === selectedCancellationReasonId
  );

  const [cancellationReasonError, setCancellationReasonError] =
    useState<boolean>(false);

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          CANCEL MY MEMBERSHIP
        </div>
      </TopNav>
      <div className="flex flex-col gap-y-7 bg-white px-8 pt-3 text-left">
        <div className="text-title">
          Are you sure you want to cancel your {membership?.membership.name}{" "}
          membership?
        </div>
        <div className="flex flex-col gap-y-5">
          <div>
            If you cancel now, you can still access your membership benefits
            until {dayjs(membership?.expiration).format("MMM D, YYYY")}. After
            this date, you will lose your membership benefits, including:
          </div>
          <ul className="list-disc pl-5 text-[22px] text-dark-grey">
            <li>
              <span className="align-middle text-base font-normal text-black">
                Membership pricing
              </span>
            </li>
            <li>
              <span className="align-middle text-base font-normal text-black">
                Membership benefits
              </span>
            </li>
            <li>
              <span className="align-middle text-base font-normal text-black">
                Monthly treatments and bonuses
              </span>
            </li>
          </ul>
          <div>
            Depending on your membership, previously earned but unclaimed
            membership treatments may still be available to claim.
          </div>
          <div>
            A confirmation email will be sent to{" "}
            <span className="text-bold1">{data.userInfo.email}</span>
          </div>
        </div>
        <div className="flex shrink">
          <Link
            to="/account/details"
            className="flex text-bold1 text-brand-color"
          >
            Change your email
          </Link>
        </div>
      </div>
      <div className="grow" />
      <div className="flex flex-col gap-y-3 px-7 py-5">
        <Dropdown
          options={cancellationReasons}
          value={selectedCancellationReasonId}
          setValue={(value) => {
            setSelectedCancellationReasonId(value);
            setCancellationReasonError(false);
          }}
          textValue={selectedCancellationReason?.name}
          label="Cancellation reason"
          invalid={cancellationReasonError}
        />
        <Button
          fullWidth
          style="dangerSolid"
          disabled={!selectedCancellationReasonId}
          onClick={() => {
            if (!selectedCancellationReasonId) {
              setCancellationReasonError(true);
              return;
            }

            navigate(
              `/wallet/memberships/manage/${membership?.membership.id}/cancel`,
              {
                state: {
                  membershipCancellationReasonId: selectedCancellationReasonId,
                },
              }
            );
          }}
        >
          Yes, cancel my membership
        </Button>
        <Button
          fullWidth
          style="secondary"
          onClick={() => navigate("/wallet/memberships")}
        >
          No, keep my membership
        </Button>
      </div>
    </div>
  );
};
